import { trpc } from "../trpc";
import {
  findFolderInTree,
  FolderTreeItem,
  insertFolderIntoTree,
  removeFolderFromTree,
} from "../folderTree";
import type { FolderTreeOutput } from "@zapier/bff";

interface MoveFolderOptions {
  optimisticUpdateEnabled?: boolean;
}

export function useMoveFolder({
  optimisticUpdateEnabled = true,
}: MoveFolderOptions) {
  const utils = trpc.useUtils();

  const mutation = trpc.folder.moveFolder.useMutation({
    // Callback to handle optimistic updates in RQ cache
    onMutate: ({ folderId, parentId, rootId }) => {
      // Update Folder under folderTree key
      utils.folder.folderTree.cancel();
      utils.folder.folderAncestors.cancel();
      utils.folder.folderShares.cancel();
      utils.folder.folderAncestors.cancel();

      // Don't optimistic update on folders listing page due to row being deleted, notification will not be shown
      if (optimisticUpdateEnabled) {
        utils.folder.folderTree.setData(
          {},
          (old: FolderTreeOutput | undefined) => {
            if (old?.hash) {
              // Grab the folder to update
              const folder = findFolderInTree(old.tree, folderId);

              // Update the folder with the new parentId and rootId
              const updatedFolder = {
                ...folder,
                rootId,
                parentId,
              } as FolderTreeItem;

              // Remove the folder from the tree
              const folderTreeWithFolderRemoved = removeFolderFromTree(
                old.tree,
                folderId,
              );

              // Insert updated folder into tree
              const updatedFolderTree = insertFolderIntoTree(
                folderTreeWithFolderRemoved,
                updatedFolder,
              );

              // this needs to return a new object ref for RQ to detect the change
              return {
                ...old,
                // update hash
                hash: {
                  ...old.hash,
                  [folderId]: updatedFolder,
                },
                // update tree
                tree: updatedFolderTree as FolderTreeItem[],
              };
            }

            return old;
          },
        );
      }
    },
    // Callback to handle cache invalidation after mutation is completed
    onSettled: () => {
      // Invalidate folderTree key to refetch data
      utils.folder.folderTree.invalidate();
      // Invalidate folderAncestors key to refetch data
      utils.folder.folderAncestors.invalidate();
      // Invalidate folderShares key to refetch data
      utils.folder.folderShares.invalidate();
      // Keep copy-preview subtree counts fresh after folder moves.
      utils.folder.listAssetsInFolderSubtree.invalidate();
      utils.folder.listFolderSubtreeMaxDepth.invalidate();
    },
  });

  return {
    moveFolder: mutation.mutate,
    isLoading: mutation.isPending,
  };
}
